import { compareObjects } from '../../utils/comparison'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { closeCheckout, getAllShippingOptions, updateCheckout } from '..'

import { CheckoutAddress, ShippingAddress, ShippingAddressStatus } from '../../model'
import { doSaveShippingAddress, doUpdateShippingAddress, getShippingAddresses, setPreferredShippingAddress } from './actions'
import { PURGE } from 'redux-persist'

export interface ShippingAddressState {
  allAddresses: ShippingAddress[]
  chosenAddress: ShippingAddress
  pickupAddress: CheckoutAddress
  isShippingAddressChanged: boolean
  shippingAddressStatus: ShippingAddressStatus
  isCheckoutUpdated: boolean
}

export const shippingAddressInitialState: ShippingAddressState = {
  allAddresses: [],
  // @ts-expect-error: OPERATION BLEED STOPPER
  chosenAddress: undefined,
  // @ts-expect-error: OPERATION BLEED STOPPER
  pickupAddress: undefined,
  isShippingAddressChanged: false,
  shippingAddressStatus: ShippingAddressStatus.Initial,
  isCheckoutUpdated: false,
}

export const sortAddressByPreferred = (allAddresses: ShippingAddress[]): ShippingAddress[] => {
  return allAddresses.sort((a, b) => (a.preferred === b.preferred ? 0 : a.preferred ? -1 : 1))
}

const changePreferredAddress = (allAddresses: ShippingAddress[], preferredId: number | string): ShippingAddress[] => {
  return allAddresses.map((address) => ({ ...address, preferred: address.id === preferredId }))
}

const slice = createSlice({
  name: 'shippingAddress',
  initialState: shippingAddressInitialState,
  reducers: {
    setChosenAddress(state: ShippingAddressState, action: PayloadAction<ShippingAddress>): ShippingAddressState {
      return {
        ...state,
        chosenAddress: action.payload,
        isShippingAddressChanged: !compareObjects(state.chosenAddress, action.payload),
      }
    },
    setPickupAddress(state: ShippingAddressState, action: PayloadAction<CheckoutAddress>): ShippingAddressState {
      return {
        ...state,
        pickupAddress: action.payload,
        isShippingAddressChanged: true,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateCheckout.pending, (state, action) => {
        state.isCheckoutUpdated = false
        state.isShippingAddressChanged = false
      })
      .addCase(updateCheckout.fulfilled, (state, action) => {
        state.isCheckoutUpdated = true
      })
      .addCase(doUpdateShippingAddress.fulfilled, (state, action) => {
        if (action.meta.arg.shippingAddressId === state.chosenAddress.id) {
          state.isShippingAddressChanged = true
        }
      })
      .addCase(getShippingAddresses.pending, (state, action) => {
        if (typeof state.chosenAddress === 'undefined') {
          state.shippingAddressStatus = ShippingAddressStatus.Initialising
        }
      })
      .addCase(getShippingAddresses.fulfilled, (state, action) => {
        const sortedAddresses = sortAddressByPreferred(action.payload)
        state.allAddresses = sortedAddresses

        if (sortedAddresses.length > 0) {
          state.chosenAddress = state.allAddresses[0]
        }

        if (state.shippingAddressStatus <= ShippingAddressStatus.Initialising) {
          state.shippingAddressStatus = ShippingAddressStatus.Initialised
          state.isShippingAddressChanged = true
        }
      })
      .addCase(getShippingAddresses.rejected, (state, action) => {
        state.shippingAddressStatus = ShippingAddressStatus.Error
      })
      .addCase(getAllShippingOptions.pending, (state, action) => {
        state.isShippingAddressChanged = false
      })
      .addCase(doSaveShippingAddress.fulfilled, (state, action) => {
        state.isShippingAddressChanged = true
        state.allAddresses.push(action.payload)
        state.chosenAddress = action.payload
      })
      .addCase(setPreferredShippingAddress.fulfilled, (state, action) => {
        state.allAddresses = sortAddressByPreferred(changePreferredAddress(state.allAddresses, action.payload))
      })
      .addCase(closeCheckout, () => shippingAddressInitialState)
      .addCase(PURGE, () => shippingAddressInitialState)
  },
})

export const { setChosenAddress, setPickupAddress } = slice.actions

export default slice.reducer
